import { BehaviorSubject, Observable, first } from 'rxjs';
import { EventBusService } from 'src/app/configurator/product/product-configuration/services/event-bus.service';
import {
  Box3Helper,
  DoubleSide,
  FrontSide,
  Matrix3,
  Matrix4,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  Path,
  Plane,
  Quaternion,
  Shape,
  ShapeGeometry,
  Vector3,
} from 'three';
import { OBB } from 'three/examples/jsm/math/OBB';
import { WallTypeEnum } from '../../interfaces/GenericInterfaces';
import { UtilityDTO } from '../DTOs/UtilityDTO';
import { ModuleProduct } from './ModuleProduct';

const WALL_OBB_THICNESS = 0.01;
export class Wall extends Object3D {
  private static _defaultNormal: Vector3 = new Vector3(0, 0, 1);
  private _mesh: Mesh;
  private _geometry: ShapeGeometry;
  private _shape: Shape;
  private _material: MeshStandardMaterial = new MeshStandardMaterial({ color: 0xe3e9ef });

  private _normal: Vector3;
  private _enableObjectVisibility = false; // Controls if the objects on the wall are always visible

  private _type: WallTypeEnum;
  private _internalType: string | undefined;

  private _width: number;
  private _height: number;

  private _scalarRepeatX = 1;
  private _scalarRepeatY = 1;
  private _materialSize = 1;

  private _plane = new Plane();
  private _wallId: string | undefined;

  private _sideFillers: Object3D[] = [];

  private _obb: OBB;
  private _utilities: UtilityDTO[];

  private _visibilityChanged: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(
    shape: Shape,
    geometry: ShapeGeometry,
    material$: Observable<MeshStandardMaterial>,
    type = WallTypeEnum.Wall,
    width: number,
    height: number,
    wallId: string | undefined = undefined,
    eventBus: EventBusService,
    utilities: UtilityDTO[],
    internalType?: string,
  ) {
    super();
    this._mesh = new Mesh(geometry, this._material);
    this._geometry = geometry;
    this._shape = shape;
    this._geometry.computeBoundingBox();
    this.add(this._mesh);

    this._type = type;
    this._height = height;
    this._width = width;

    const rotationMatrix = new Matrix3();
    const obbPosition = this.position.clone();
    obbPosition.z -= WALL_OBB_THICNESS;
    this._obb = new OBB(obbPosition, new Vector3(this._width / 2, this._height / 2, WALL_OBB_THICNESS), rotationMatrix);

    this._wallId = wallId;
    this._internalType = internalType;
    this._utilities = utilities;

    this._normal = Wall._defaultNormal.clone().applyQuaternion(this.quaternion);
    this._updateNormal();
    material$.pipe(first()).subscribe((material) => {
      if (material.map?.repeat) {
        this._scalarRepeatX = material.map.repeat.x;
        this._scalarRepeatY = material.map.repeat.y;
        this._materialSize = Math.max(this._scalarRepeatX, this._scalarRepeatY);
      }

      if (this.type === WallTypeEnum.Wall) {
        if (this.internalType === 'exterior') {
          material.side = FrontSide;
        } else {
          material.side = DoubleSide;
        }
      }

      this.setMaterial(material, this._materialSize);
      eventBus.publicWallTextureChange();
    });
  }

  private _updatePlane() {
    this._plane.setFromNormalAndCoplanarPoint(this.getNormal(), this.position);

    this.updateMatrix();
    const rotMatrix4 = new Matrix4();
    rotMatrix4.makeRotationFromQuaternion(this.quaternion);
    const wallCenter = new Vector3(0, 0, -WALL_OBB_THICNESS);
    wallCenter.applyMatrix4(this.matrix);
    const rotationMatrix = new Matrix3().setFromMatrix4(rotMatrix4);

    this._obb = new OBB(wallCenter, new Vector3(this._width / 2, this._height / 2, WALL_OBB_THICNESS), rotationMatrix);
  }

  private _updateNormal() {
    this._normal = Wall._defaultNormal.clone().applyQuaternion(this.quaternion);
    this._updatePlane();
  }

  public setVisibility(visible: boolean) {
    if (this.visible !== visible) {
      this._visibilityChanged.next(visible);
      this.visible = visible;
      this._sideFillers.forEach((sideFiller) => (sideFiller.visible = visible));
    }
  }

  public isVisible() {
    return this.visible;
    //return this.scale.equals(new Vector3(1, 1, 1));
  }

  public getNormal(): Vector3 {
    return this._normal.clone();
  }

  public setPosition(x: number, y: number, z: number) {
    this.position.set(x, y, z);
    this._updatePlane();
  }

  public setQuaternion(quaternion: Quaternion) {
    this.quaternion.copy(quaternion);
    this._updateNormal();
  }

  public getDefaultNormal() {
    return Wall._defaultNormal.clone();
  }

  private _updateShape(object: ModuleProduct) {
    object.updateMatrix();
    object.updateMatrixWorld();
    object.updateBoundingBox();

    const bbox = object.boundingBox;

    const boxMin = bbox.min;
    const boxMax = bbox.max;

    const asd = new Box3Helper(bbox, '#ff0000');
    object.add(asd);

    const hole = new Path();
    hole.moveTo(boxMin.x, boxMin.y);
    hole.lineTo(boxMax.x, boxMin.y);
    hole.lineTo(boxMax.x, boxMax.y);
    hole.lineTo(boxMin.x, boxMax.y);
    hole.lineTo(boxMin.x, boxMin.y);

    this._shape.holes = [hole];

    const newGeometry = new ShapeGeometry(this._shape);

    this._mesh.geometry.dispose();
    this._geometry.dispose();
    this._geometry = newGeometry;
    this._mesh.geometry = this._geometry;
  }

  public getMinVector(): Vector3 {
    const dimVector = new Vector3(this._width / -2, this._height / -2, 0);
    //dimVector.applyQuaternion(this.quaternion); // Apply the wall's rotation
    //dimVector.add(this.position);
    //dimVector.applyMatrix4(this.matrixWorld);
    return dimVector;
  }

  public getMaxVector(): Vector3 {
    const dimVector = new Vector3(this._width / 2, this._height / 2, 0);
    //dimVector.applyQuaternion(this.quaternion); // Apply the wall's rotation
    //dimVector.add(this.position);
    //dimVector.applyMatrix4(this.matrixWorld);
    return dimVector;
  }

  public setMaterial(material: MeshStandardMaterial, materialSize: number) {
    this._materialSize = materialSize;
    this._material = material;
    this._mesh.material = material;
    this._mesh.material.needsUpdate = true;

    this.setRepeat();
  }

  public setRepeat() {
    this._material.map?.repeat.set(this._materialSize, this._materialSize);
    this._material.normalMap?.repeat.set(this._materialSize, this._materialSize);
    this._material.roughnessMap?.repeat.set(this._materialSize, this._materialSize);
    this._material.displacementMap?.repeat.set(this._materialSize, this._materialSize);
  }

  public updateColor(code: number) {
    this._material.color.setHex(code);
  }

  public setCastShadow(castShadow: boolean) {
    this._mesh.castShadow = castShadow;
  }

  public setReceiveShadow(receiveShadow: boolean) {
    this._mesh.receiveShadow = receiveShadow;
  }

  public setScale(x: number, y: number, z: number) {
    this._mesh.scale.set(x, y, z);
  }

  public getMesh() {
    return this._mesh;
  }

  public getBoundingBox() {
    return this._mesh.geometry.boundingBox;
  }

  public setScalarRepeat(x: number, y: number) {
    this._scalarRepeatX = x;
    this._scalarRepeatY = y;
  }

  public get wallId() {
    return this._wallId;
  }

  public get wallType() {
    return this._type;
  }

  public get height() {
    return this._height;
  }

  public get width() {
    return this._width;
  }

  public get plane() {
    return this._plane;
  }

  public get sideFillers() {
    return this._sideFillers;
  }

  public set sideFillers(value) {
    this._sideFillers = value;
  }

  public get internalType(): string | undefined {
    return this._internalType;
  }

  public set internalType(value: string | undefined) {
    this._internalType = value;
  }

  public get enableObjectVisibility() {
    return this._enableObjectVisibility;
  }

  public set enableObjectVisibility(value) {
    this._enableObjectVisibility = value;
  }

  public get visibilityChanged$() {
    return this._visibilityChanged.asObservable();
  }

  public getOBB(): OBB {
    //TODO: POGRUNTAJ KAK BOMO RESLI POD
    if (this._type === WallTypeEnum.Floor) return new OBB(undefined, undefined, undefined);
    return this._obb;
  }

  public getUtilities(): UtilityDTO[] {
    return this._utilities;
  }
}
